import type { RowBoundary, AreaBoundary, ViewState, Place } from '../types/warehouse.types';
import { Theme } from '@material-ui/core';
import { drawPolygon } from '../utils/canvasDrawing';
import { BaseRenderer } from './base/BaseRenderer';
import { RowLabelRenderer } from './RowLabelRenderer';

export interface RowRendererConfig {
  fillOpacity: string; // Hex opacity suffix (e.g., '22' for ~13%)
  borderLineWidth: number;
  borderLineDash: number[];
  // Label config
  showLabel: boolean;
  labelFormat: (row: RowBoundary) => string;
  labelStrokeWidth: number;
  // Hover config
  hoverBorderLineWidth: number;
  // Selection config
  selectedBorderLineWidth: number;
  selectedFillOpacity: string;
}

export class RowRenderer extends BaseRenderer<RowRendererConfig> {
  private labelRenderer: RowLabelRenderer;

  constructor(theme: Theme, config?: Partial<RowRendererConfig>) {
    const defaultConfig: RowRendererConfig = {
      fillOpacity: '22',
      borderLineWidth: 1,
      borderLineDash: [5, 5],
      showLabel: true,
      labelFormat: (row: RowBoundary) => {
        const match = row.name.match(/R(\d+)/);
        const displayName = match ? `R${match[1]}` : row.name;
        const displayTitle = row.label ? `${displayName} • ${row.label}` : displayName;
        return `Row: ${displayTitle}`;
      },
      labelStrokeWidth: 2,
      hoverBorderLineWidth: 3,
      selectedBorderLineWidth: 4,
      selectedFillOpacity: '44',
    };
    super(theme, defaultConfig, config);
    this.labelRenderer = new RowLabelRenderer(theme);
  }

  /**
   * Clear caches (call when layout changes)
   */
  public clearCache(): void {
    this.labelRenderer.clearCache();
  }

  render(
    ctx: CanvasRenderingContext2D,
    rows: RowBoundary[],
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    hoveredRow: RowBoundary | null = null,
    places: Map<string, Place>,
    areaBoundaries: AreaBoundary[] = [],
    selectedRows: Set<string> = new Set(),
    canvasSize?: { width: number; height: number } // Added for viewport culling
  ): void {
    const {
      fillOpacity,
      borderLineWidth,
      borderLineDash,
      hoverBorderLineWidth,
      selectedBorderLineWidth,
      selectedFillOpacity,
    } = this.config;

    const renderStart = performance.now();
    let rowsRendered = 0;
    let rowsCulled = 0;

    // Render row polygons
    rows.forEach((row) => {
      if (row.polygon.length < 2) return;

      // Convert world coordinates to screen coordinates
      const screenPoints = row.polygon.map(point => worldToScreen(point.x, point.y));

      // Viewport culling: Check if row polygon is completely outside canvas
      if (canvasSize) {
        // Calculate bounding box of the row polygon in screen space
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const point of screenPoints) {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        }

        // Add buffer for partial visibility
        const buffer = 50;

        // Skip if completely outside viewport
        if (
          maxX < -buffer ||
          minX > canvasSize.width + buffer ||
          maxY < -buffer ||
          minY > canvasSize.height + buffer
        ) {
          rowsCulled++;
          return;
        }
      }

      rowsRendered++;

      // Determine state
      const isSelected = selectedRows.has(row.name);
      const isHovered = hoveredRow === row;

      // Determine fill opacity
      const opacity = isSelected ? selectedFillOpacity : fillOpacity;

      // Determine border styling
      let strokeColor: string;
      let strokeWidth: number;
      let lineDash: number[];

      if (isSelected) {
        // Priority: row-specific > default
        strokeColor = row.rowSelectionBorderColor || this.theme.palette.storageUi.textPrimary;
        strokeWidth = row.rowSelectionBorderWidth || selectedBorderLineWidth;
        lineDash = []; // Solid line for selected
      } else if (isHovered) {
        strokeColor = this.theme.palette.secondary.main;
        strokeWidth = hoverBorderLineWidth;
        lineDash = borderLineDash;
      } else {
        strokeColor = row.borderColor;
        strokeWidth = borderLineWidth;
        lineDash = borderLineDash;
      }

      // Set line dash before drawing
      ctx.setLineDash(lineDash);

      // Add glow effect for selected rows using shadowBlur = strokeWidth * 2
      if (isSelected) {
        ctx.shadowColor = strokeColor;
        ctx.shadowBlur = strokeWidth * 2;
      }

      // Draw polygon with fill and border
      drawPolygon(ctx, screenPoints, {
        fill: true,
        stroke: true,
        closePath: true,
        style: {
          fillStyle: row.color + opacity,
          strokeStyle: strokeColor,
          lineWidth: strokeWidth
        }
      });

      // Reset shadow
      ctx.shadowBlur = 0;

      // Reset line dash
      ctx.setLineDash([]);
    });

    if (this.config.showLabel) {
      this.labelRenderer.render(
        ctx,
        rows,
        viewState,
        worldToScreen,
        places,
        areaBoundaries,
        {
          showLabel: this.config.showLabel,
          labelFormat: this.config.labelFormat,
          labelStrokeWidth: this.config.labelStrokeWidth
        },
        canvasSize
      );
    }

    const renderTime = performance.now() - renderStart;
    if (renderTime > 10 || rows.length > 500) {
      console.log(`[RowRenderer] ${renderTime.toFixed(1)}ms | Total: ${rows.length} | Rendered: ${rowsRendered} | Culled: ${rowsCulled}`);
    }
  }
}